/* ==========================================================================
   YUNSO — fenêtre infolettre

   Ouverture au premier des deux déclencheurs atteint (délai ou profondeur).

   Quand la section porte une question, la fenêtre s'ouvre dessus : un clic
   sur une réponse ajoute l'étiquette au formulaire et passe à l'adresse.

   L'envoi est une navigation classique : la protection anti-robots de Shopify
   ajoute son jeton au moment de la soumission native et refuse toute requête
   en arrière-plan (400 Missing CAPTCHA token). Le visiteur revient donc sur la
   même page, où Liquid a marqué la racine avec data-posted ; le script rouvre
   alors la fenêtre directement sur le code.
   ========================================================================== */

(function () {
  'use strict';

  var STORAGE_KEY = 'yunso:newsletter-popup';
  var PENDING_KEY = 'yunso:newsletter-popup-pending';
  var ANSWER_KEY = 'yunso:newsletter-popup-answer';
  var designMode = Boolean(window.Shopify && window.Shopify.designMode);

  /* ------------------------------------------------------------- Mémoire */

  // Le stockage échoue en navigation privée sur certains navigateurs, et
  // l'exception ne doit jamais empêcher la fenêtre de fonctionner.
  function readState() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch (error) {
      return {};
    }
  }

  function writeState(patch) {
    try {
      var next = readState();
      Object.keys(patch).forEach(function (key) { next[key] = patch[key]; });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (error) {
      /* Sans mémoire, la fenêtre se represente : gênant, jamais bloquant. */
    }
  }

  function isPending() {
    try {
      return sessionStorage.getItem(PENDING_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  function clearPending() {
    try {
      sessionStorage.removeItem(PENDING_KEY);
      sessionStorage.removeItem(ANSWER_KEY);
    } catch (error) {
      /* Rien à nettoyer : le marqueur n'a jamais pu être posé. */
    }
  }

  // La réponse doit survivre au rechargement d'un envoi refusé : sans elle,
  // le second envoi partirait sans l'étiquette.
  function rememberAnswer(id) {
    try {
      sessionStorage.setItem(ANSWER_KEY, id);
    } catch (error) {
      /* L'adresse partira sans étiquette en cas d'erreur : rien de bloquant. */
    }
  }

  function recallAnswer() {
    try {
      return sessionStorage.getItem(ANSWER_KEY);
    } catch (error) {
      return null;
    }
  }

  /* --------------------------------------------------------------- Écran */

  function init(root) {
    var popup = (root || document).querySelector('[data-newsletter-popup]');
    if (!popup || popup.dataset.bound === 'true') return;
    popup.dataset.bound = 'true';

    var dialog = popup.querySelector('.np__dialog');
    var form = popup.querySelector('form');
    var panels = popup.querySelectorAll('[data-newsletter-popup-panel]');
    var answers = popup.querySelectorAll('[data-newsletter-popup-answer]');
    var replies = popup.querySelectorAll('[data-newsletter-popup-reply]');
    var backButton = popup.querySelector('[data-newsletter-popup-back]');
    var successTitle = popup.querySelector('[data-newsletter-popup-success-title]');
    var copyButton = popup.querySelector('[data-newsletter-popup-copy]');
    if (!dialog || !form) return;

    var tagsInput = form.querySelector('[data-newsletter-popup-tags]');
    var emailInput = form.querySelector('input[type="email"]');
    var baseTags = tagsInput ? tagsInput.value : '';
    var firstStep = answers.length ? 'question' : 'offer';
    var chosen = null;

    var delay = parseInt(popup.dataset.delay, 10) || 0;
    var depth = parseInt(popup.dataset.scroll, 10) || 0;
    var remindDays = parseInt(popup.dataset.remindDays, 10) || 0;
    var hideOnCart = popup.dataset.hideOnCart === 'true';

    var timer = null;
    var lastFocused = null;
    var isOpen = false;
    var protectedForm = false;

    /* --------------------------------------------------- Anti-robots */

    // La protection anti-robots de Shopify n'équipe au chargement que les
    // formulaires visibles : le nôtre est dans une fenêtre masquée, elle le
    // saute, et l'envoi part sans jeton (400 Missing CAPTCHA token). Cette
    // API est prévue pour les formulaires apparus après coup.
    function protectForm() {
      if (protectedForm) return;
      var captcha = window.Shopify && window.Shopify.captcha;
      if (!captcha || typeof captcha.protect !== 'function') return;
      protectedForm = true;
      captcha.protect(form);
    }

    /* ------------------------------------------------------ Ouverture */

    function open() {
      if (isOpen) return;
      disarm();
      isOpen = true;
      lastFocused = document.activeElement;
      popup.hidden = false;
      // Une lecture forcée entre l'affichage et l'état ouvert : sans elle, le
      // navigateur regroupe les deux et la transition ne joue pas.
      void popup.offsetHeight;
      popup.dataset.open = 'true';
      document.body.classList.add('is-locked');
      // Équipé dès l'ouverture : le visiteur met plusieurs secondes à saisir
      // son adresse, le widget a le temps de se charger avant l'envoi.
      protectForm();

      // Le focus va sur le dialogue, pas sur le champ : les lecteurs d'écran
      // annoncent le titre, et le clavier mobile ne surgit pas d'office.
      dialog.focus({ preventScroll: true });
    }

    function close(remember) {
      if (!isOpen) return;
      isOpen = false;
      popup.dataset.open = 'false';
      document.body.classList.remove('is-locked');

      if (remember && remindDays > 0) {
        writeState({ dismissedUntil: Date.now() + remindDays * 86400000 });
      }

      // Rouverte entre-temps (éditeur de thème), la fenêtre ne doit pas être
      // masquée par le minuteur de la fermeture précédente.
      var done = function () { if (!isOpen) popup.hidden = true; };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) done();
      else window.setTimeout(done, 240);

      if (lastFocused && lastFocused.focus) lastFocused.focus({ preventScroll: true });
    }

    function showStep(name) {
      Array.prototype.forEach.call(panels, function (panel) {
        panel.hidden = panel.dataset.newsletterPopupPanel !== name;
      });
      dialog.dataset.step = name;
    }

    /* ------------------------------------------------------- Question */

    function choose(answer) {
      chosen = answer;
      if (tagsInput) tagsInput.value = baseTags + ', ' + answer.dataset.tag;
      Array.prototype.forEach.call(replies, function (reply) {
        reply.hidden = reply.dataset.newsletterPopupReply !== answer.dataset.newsletterPopupAnswer;
      });
      rememberAnswer(answer.dataset.newsletterPopupAnswer);
      showStep('offer');
    }

    function findAnswer(id) {
      return Array.prototype.filter.call(answers, function (answer) {
        return answer.dataset.newsletterPopupAnswer === id;
      })[0];
    }

    Array.prototype.forEach.call(answers, function (answer) {
      answer.addEventListener('click', function () {
        choose(answer);
        // Le visiteur vient de s'engager : on lui tend le champ, quitte à
        // faire monter le clavier mobile, c'est la suite qu'il attend.
        if (emailInput) emailInput.focus({ preventScroll: true });
      });
    });

    if (backButton) {
      backButton.addEventListener('click', function () {
        showStep('question');
        if (chosen) chosen.focus({ preventScroll: true });
      });
    }

    /* ---------------------------------------------------- Déclencheurs */

    function scrollDepth() {
      var max = document.documentElement.scrollHeight - window.innerHeight;
      if (max <= 0) return 0;
      return (window.scrollY / max) * 100;
    }

    function onScroll() {
      if (scrollDepth() >= depth) open();
    }

    function disarm() {
      if (timer) window.clearTimeout(timer);
      timer = null;
      window.removeEventListener('scroll', onScroll);
    }

    function blocked() {
      // Dans l'éditeur de thème, la fenêtre s'ouvre à la sélection de la
      // section : ni les compteurs ni la mémoire ne doivent s'en mêler.
      if (designMode) return true;

      // Envoi en cours : Shopify peut intercaler sa page de vérification
      // anti-robots. Y faire surgir la fenêtre n'aurait aucun sens.
      if (isPending()) return true;

      var state = readState();
      if (state.subscribed) return true;
      if (state.dismissedUntil && Date.now() < state.dismissedUntil) return true;
      if (hideOnCart && /^\/cart(\/|$)/.test(window.location.pathname)) return true;
      return false;
    }

    function arm() {
      if (blocked()) return;
      if (delay > 0) timer = window.setTimeout(open, delay * 1000);
      if (depth > 0) {
        window.addEventListener('scroll', onScroll, { passive: true });
        // Page déjà défilée au chargement (retour arrière, ancre) : le seuil
        // peut être franchi avant le moindre événement de défilement.
        onScroll();
      }
    }

    /* ---------------------------------------------------------- Envoi */

    form.addEventListener('submit', function () {
      // Marque le départ pour se reconnaître au retour : le formulaire du
      // pied de page poste le même form_type, et lui ne doit rien rouvrir.
      try {
        sessionStorage.setItem(PENDING_KEY, '1');
      } catch (error) {
        /* Sans marque, le visiteur revient sans la fenêtre : il reste inscrit. */
      }
    });

    /* ---------------------------------------------------------- Copie */

    if (copyButton) {
      // Sans presse-papiers accessible, le bouton ne tiendrait pas sa promesse.
      // Le code reste lisible et sélectionnable à côté : mieux vaut le retirer.
      if (!navigator.clipboard) {
        copyButton.hidden = true;
      } else {
        copyButton.addEventListener('click', function () {
          var code = popup.querySelector('[data-newsletter-popup-code]');
          if (!code) return;

          navigator.clipboard
            .writeText(code.textContent.trim())
            .then(function () {
              copyButton.textContent = copyButton.dataset.labelCopied;
              window.setTimeout(function () {
                copyButton.textContent = copyButton.dataset.labelCopy;
              }, 2000);
            })
            .catch(function () {
              // Refusé par le navigateur (document sans focus, permission) :
              // on sélectionne le code pour que la copie reste possible.
              var range = document.createRange();
              range.selectNodeContents(code);
              var selection = window.getSelection();
              selection.removeAllRanges();
              selection.addRange(range);
            });
        });
      }
    }

    /* ------------------------------------------------------ Fermeture */

    popup.querySelectorAll('[data-newsletter-popup-close]').forEach(function (el) {
      el.addEventListener('click', function () { close(true); });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && isOpen) close(true);
    });

    // Piège de focus : la fenêtre est un dialogue modal.
    popup.addEventListener('keydown', function (event) {
      if (event.key !== 'Tab' || !isOpen) return;

      var focusables = Array.prototype.filter.call(
        dialog.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ),
        function (el) { return el.offsetParent !== null; }
      );
      if (!focusables.length) return;

      var first = focusables[0];
      var last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    /* -------------------------------------------------------- Éditeur */

    if (designMode) {
      document.addEventListener('shopify:section:select', function (event) {
        if (!event.target.contains(popup)) return;
        showStep(firstStep);
        open();
      });
      // Sélectionner une réponse dans l'éditeur montre la phrase qui lui
      // correspond, sur le panneau de l'adresse.
      document.addEventListener('shopify:block:select', function (event) {
        if (!popup.contains(event.target)) return;
        var answer = event.target.closest('[data-newsletter-popup-answer]');
        if (!answer) return;
        open();
        choose(answer);
      });
      document.addEventListener('shopify:section:deselect', function (event) {
        if (event.target.contains(popup)) close(false);
      });
    }

    /* ------------------------------------------------- Retour d'envoi */

    // Le marqueur n'est consommé que sur la page qui porte le verdict. Shopify
    // peut intercaler une page de vérification anti-robots : l'y effacer ferait
    // perdre le code au visiteur qui vient pourtant de s'inscrire.
    if (popup.dataset.posted === 'true') {
      // Inscrit : plus jamais de relance, que l'adresse soit partie d'ici ou
      // du pied de page.
      writeState({ subscribed: true });
      if (isPending()) {
        clearPending();
        showStep('success');
        open();
        if (successTitle) successTitle.focus({ preventScroll: true });
      }
      return;
    }

    // Refusée (adresse invalide, refus de Shopify) : on rouvre sur le message
    // d'erreur que Liquid vient de rendre, plutôt que de laisser le visiteur
    // devant une page rechargée sans explication.
    // La question est déjà répondue : on revient directement sur l'adresse.
    if (popup.dataset.hasErrors === 'true' && isPending()) {
      var previous = findAnswer(recallAnswer());
      clearPending();
      if (previous) choose(previous);
      else showStep('offer');
      open();
      return;
    }

    arm();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
